<template>
  <div class="payment-complete-view">
    <AppHeader />
    
    <div class="complete-container">
      <!-- Loading State - Verifying Payment -->
      <div v-if="isVerifying" class="verifying-state">
        <div class="spinner-lg"></div>
        <p>{{ $t('verifyingPayment') }}</p>
      </div>

      <!-- Success State -->
      <div v-else-if="paymentSuccess" class="success-state">
        <div class="success-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h1>{{ $t('paymentSuccess') }}</h1>
        <p class="success-message">{{ $t('paymentSuccessMessage') }}</p>
        
        <div class="order-details">
          <div class="detail-row">
            <span class="detail-label">{{ $t('orderNumber') }}</span>
            <span class="detail-value">{{ orderNumber }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">{{ $t('table') }}</span>
            <span class="detail-value">{{ tableId }}</span>
          </div>
          <div v-if="totalAmount" class="detail-row total">
            <span class="detail-label">{{ $t('total') }}</span>
            <span class="detail-value">{{ formatPrice(totalAmount) }}</span>
          </div>
        </div>

        <div class="next-steps">
          <p class="next-steps-text">{{ $t('orderBeingPrepared') }}</p>
        </div>

        <div class="actions">
          <button @click="orderMore" class="btn-primary">{{ $t('orderMore') }}</button>
          <button @click="endSession" class="btn-secondary">{{ $t('endSession') }}</button>
        </div>
      </div>

      <!-- Failure State -->
      <div v-else-if="paymentFailed" class="failure-state">
        <div class="failure-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M15 9l-6 6M9 9l6 6"/>
          </svg>
        </div>
        <h1>{{ $t('paymentFailed') }}</h1>
        <p class="failure-message">{{ errorMessage || $t('paymentFailedMessage') }}</p>
        
        <div class="actions">
          <button @click="retryPayment" class="btn-primary">{{ $t('tryAgain') }}</button>
          <button @click="goBackToCheckout" class="btn-secondary">{{ $t('backToCheckout') }}</button>
        </div>
      </div>

      <!-- Cancelled State -->
      <div v-else-if="paymentCancelled" class="cancelled-state">
        <div class="cancelled-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
        </div>
        <h1>{{ $t('paymentCancelled') }}</h1>
        <p>{{ $t('paymentCancelledMessage') }}</p>
        
        <div class="actions">
          <button @click="retryPayment" class="btn-primary">{{ $t('tryAgain') }}</button>
          <button @click="goBackToMenu" class="btn-secondary">{{ $t('backToMenu') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useTableStore } from '@/stores/table'
import { useSessionStore } from '@/stores/session'
import { formatPrice } from '@/utils/formatters'
import AppHeader from '@/components/AppHeader.vue'

const router = useRouter()
const route = useRoute()
const tableStore = useTableStore()
const sessionStore = useSessionStore()

const isVerifying = ref(true)
const paymentSuccess = ref(false)
const paymentFailed = ref(false)
const paymentCancelled = ref(false)
const errorMessage = ref(null)
const orderNumber = ref('')
const totalAmount = ref(0)

const tableId = computed(() => sessionStore.tableId)

// Firebase Cloud Functions URL
const FUNCTIONS_BASE_URL = process.env.VUE_APP_FUNCTIONS_URL || 
  'https://europe-west1-dandero-3dff1.cloudfunctions.net'

async function verifyPayment() {
  isVerifying.value = true

  try {
    // Get status from query params (set by Dintero callback)
    const status = route.query.status
    const merchantReference = route.query.merchant_reference
    const transactionId = route.query.transaction_id
    const error = route.query.error

    // Handle cancelled payment
    if (status === 'cancelled') {
      paymentCancelled.value = true
      isVerifying.value = false
      return
    }

    // Handle explicit error
    if (error) {
      errorMessage.value = error
      paymentFailed.value = true
      isVerifying.value = false
      return
    }

    // If we have a transaction ID, verify the payment status via backend
    if (transactionId) {
      const response = await fetch(`${FUNCTIONS_BASE_URL}/getPaymentStatus?transactionId=${transactionId}`)
      
      if (response.ok) {
        const data = await response.json()
        
        if (data.status === 'AUTHORIZED' || data.status === 'CAPTURED') {
          paymentSuccess.value = true
          orderNumber.value = merchantReference || transactionId.slice(-8).toUpperCase()
          totalAmount.value = data.amount || tableStore.cartTotal
          
          // Clear the cart after successful payment
          await tableStore.completeOrder(transactionId)
        } else if (data.status === 'FAILED' || data.status === 'REJECTED') {
          paymentFailed.value = true
          errorMessage.value = data.error || 'Payment was not approved'
        } else {
          // Payment still processing
          paymentSuccess.value = true
          orderNumber.value = merchantReference || transactionId.slice(-8).toUpperCase()
          totalAmount.value = tableStore.cartTotal
          await tableStore.completeOrder(transactionId)
        }
      } else {
        // API call failed, but payment might still be successful
        // Trust the frontend callback status
        if (status === 'success') {
          paymentSuccess.value = true
          orderNumber.value = merchantReference || 'Unknown'
          totalAmount.value = tableStore.cartTotal
          await tableStore.completeOrder(transactionId)
        } else {
          paymentFailed.value = true
          errorMessage.value = 'Could not verify payment status'
        }
      }
    } else if (status === 'success') {
      // No transaction ID but success status from redirect
      paymentSuccess.value = true
      orderNumber.value = merchantReference || 'Confirmed'
      totalAmount.value = tableStore.cartTotal
      await tableStore.completeOrder(null)
    } else {
      // No clear status, assume failure
      paymentFailed.value = true
      errorMessage.value = 'Payment could not be processed'
    }
  } catch (err) {
    console.error('Payment verification error:', err)
    paymentFailed.value = true
    errorMessage.value = err.message || 'Failed to verify payment'
  }

  isVerifying.value = false
}

function retryPayment() {
  const slug = sessionStore.restaurantSlug || 'demo'
  router.push(`/${slug}/payment`)
}

function orderMore() {
  const slug = sessionStore.restaurantSlug || 'demo'
  router.push(`/${slug}/menu`)
}

function goBackToCheckout() {
  const slug = sessionStore.restaurantSlug || 'demo'
  router.push(`/${slug}/checkout`)
}

function goBackToMenu() {
  const slug = sessionStore.restaurantSlug || 'demo'
  router.push(`/${slug}/menu`)
}

function endSession() {
  const slug = sessionStore.restaurantSlug || 'demo'
  sessionStore.leaveSession()
  tableStore.clearLocalState()
  router.push(`/${slug}`)
}

onMounted(() => {
  verifyPayment()
})
</script>

<style scoped>
.payment-complete-view {
  min-height: 100vh;
  background: var(--bg-primary);
}

.complete-container {
  max-width: 500px;
  margin: 0 auto;
  padding: 48px 16px;
}

/* Shared State Styles */
.verifying-state,
.success-state,
.failure-state,
.cancelled-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.spinner-lg {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-color);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Success State */
.success-state .success-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  animation: scaleIn 0.3s ease-out;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.success-icon svg {
  width: 40px;
  height: 40px;
  stroke: white;
  stroke-width: 3;
}

.success-state h1 {
  font-size: 1.75rem;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.success-message {
  color: var(--text-secondary);
  font-size: 1rem;
  margin-bottom: 32px;
}

/* Order Details */
.order-details {
  width: 100%;
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
}

.detail-row:not(:last-child) {
  border-bottom: 1px solid var(--border-color);
}

.detail-label {
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.detail-value {
  color: var(--text-primary);
  font-weight: 600;
}

.detail-row.total {
  padding-top: 12px;
  margin-top: 4px;
}

.detail-row.total .detail-value {
  font-size: 1.25rem;
  color: var(--primary-color);
}

/* Next Steps */
.next-steps {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 32px;
  width: 100%;
}

.next-steps-text {
  color: #047857;
  font-size: 0.95rem;
  margin: 0;
}

/* Failure State */
.failure-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.failure-icon svg {
  width: 40px;
  height: 40px;
  stroke: white;
  stroke-width: 2.5;
}

.failure-state h1 {
  font-size: 1.75rem;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.failure-message {
  color: var(--text-secondary);
  margin-bottom: 32px;
}

/* Cancelled State */
.cancelled-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.cancelled-icon svg {
  width: 40px;
  height: 40px;
  stroke: white;
  stroke-width: 2.5;
}

.cancelled-state h1 {
  font-size: 1.75rem;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.cancelled-state p {
  color: var(--text-secondary);
  margin-bottom: 32px;
}

/* Actions */
.actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 300px;
}

.btn-primary {
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 14px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  width: 100%;
}

.btn-primary:hover {
  background: var(--primary-hover);
}

.btn-secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  padding: 14px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  width: 100%;
}

.btn-secondary:hover {
  background: var(--bg-tertiary);
}

/* Mobile Responsiveness */
@media (max-width: 480px) {
  .complete-container {
    padding: 32px 16px;
  }

  .success-state h1,
  .failure-state h1,
  .cancelled-state h1 {
    font-size: 1.5rem;
  }

  .success-icon,
  .failure-icon,
  .cancelled-icon {
    width: 64px;
    height: 64px;
  }

  .success-icon svg,
  .failure-icon svg,
  .cancelled-icon svg {
    width: 32px;
    height: 32px;
  }
}
</style>
